import React, { useState, useEffect } from 'react';
import { getUsers, setUserDerivToken, getUserDerivToken, deleteUser } from '../../services/userService';
import { PencilSquareIcon, CheckIcon, XMarkIcon, UserIcon, TrashIcon } from '@heroicons/react/24/outline';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingUserId, setEditingUserId] = useState(null);
    const [tokenInput, setTokenInput] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userTokens, setUserTokens] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingUserId, setDeletingUserId] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        // Filter users based on search term
        const term = searchTerm.toLowerCase();
        const filtered = users.filter(u =>
            u.name.toLowerCase().includes(term) ||
            u.email.toLowerCase().includes(term) ||
            u.role.toLowerCase().includes(term)
        );
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    const fetchUserToken = async (userId) => {
        try {
            const token = await getUserDerivToken(userId);
            setUserTokens(prev => ({ ...prev, [userId]: token }));
        } catch (err) {
            if (err.response?.status !== 404) {
                console.error(`Error fetching token for user ${userId}:`, err);
            }
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getUsers();
            const usersList = Array.isArray(data) ? data : [];
            setUsers(usersList);
            setFilteredUsers(usersList);

            usersList.forEach(user => {
                if (user.id) fetchUserToken(user.id);
            });
        } catch (err) {
            setError(err.message || 'Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (user) => {
        setEditingUserId(user.id);
        setTokenInput(user.deriv_token || '');
        setError('');
        setSuccess('');
    };

    const handleCancel = () => {
        setEditingUserId(null);
        setTokenInput('');
        setError('');
        setSuccess('');
    };

    const handleSave = async (userId) => {
        if (!tokenInput.trim()) {
            setError('Token cannot be empty');
            return;
        }

        try {
            setLoading(true);
            setError('');
            setSuccess('');

            const response = await setUserDerivToken(tokenInput, userId);
            if (response && response.success) {
                setSuccess('Token updated successfully');
                setUserTokens(prev => ({ ...prev, [userId]: tokenInput }));
                setEditingUserId(null);
                setTokenInput('');
                await fetchUserToken(userId);
            } else {
                throw new Error(response?.message || 'Failed to update token');
            }
        } catch (err) {
            setError(err.message || 'Failed to update token. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (userId, userName) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`);
        
        if (!confirmDelete) {
            return;
        }

        try {
            setDeletingUserId(userId);
            setError('');
            setSuccess('');

            const response = await deleteUser(userId);
            if (response && response.success) {
                setSuccess(`User "${userName}" deleted successfully`);
                // Remove user from the list
                setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
                setFilteredUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
            } else {
                throw new Error(response?.message || 'Failed to delete user');
            }
        } catch (err) {
            setError(err.message || 'Failed to delete user. Please try again.');
        } finally {
            setDeletingUserId(null);
        }
    };

    if (loading && users.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">User Management</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-300">
                        Manage user tokens for Deriv API access
                    </p>
                </div>

                {/* Search Bar */}
                <div className="mt-3 md:mt-0">
                    <input
                        type="text"
                        placeholder="Search by name, email, or role..."
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {error && <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-4">{error}</div>}
            {success && <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">{success}</div>}

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Deriv Token</th>
                        <th className="px-6 py-3 relative"><span className="sr-only">Actions</span></th>
                    </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                                        <UserIcon className="h-6 w-6" />
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">ID: {user.id}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{user.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.role === 'admin'
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100'
                          : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100'
                  }`}>{user.role}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {editingUserId === user.id ? (
                                    <input
                                        type="text"
                                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        value={tokenInput}
                                        onChange={(e) => setTokenInput(e.target.value)}
                                        placeholder="Enter Deriv API token"
                                    />
                                ) : (
                                    <span className="font-mono text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                      {userTokens[user.id] || 'No token set'}
                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                {editingUserId === user.id ? (
                                    <div className="flex space-x-2 justify-end">
                                        <button onClick={() => handleSave(user.id)} className="text-green-600 hover:text-green-900" disabled={loading}><CheckIcon className="h-5 w-5" /></button>
                                        <button onClick={handleCancel} className="text-red-600 hover:text-red-900" disabled={loading}><XMarkIcon className="h-5 w-5" /></button>
                                    </div>
                                ) : (
                                    <div className="flex space-x-2 justify-end">
                                        <button 
                                            onClick={() => handleEditClick(user)} 
                                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" 
                                            disabled={loading || deletingUserId === user.id}
                                            title="Edit token"
                                        >
                                            <PencilSquareIcon className="h-5 w-5" />
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(user.id, user.name)} 
                                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300" 
                                            disabled={loading || deletingUserId === user.id}
                                            title="Delete user"
                                        >
                                            {deletingUserId === user.id ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-red-600"></div>
                                            ) : (
                                                <TrashIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UserManagement;
